import Typography from '@components/v2/ui/Typography/Typography';
import { Fragment } from 'react';
import { useTranslation } from '../../translation/TranslationContext';
import type { ActivityFeedChildOrder } from '../utils/activityFeedPhrase.types';

interface ActivityFeedChildOrdersProps {
  childOrders: ActivityFeedChildOrder[];
}

interface ChildOrderSegment extends ActivityFeedChildOrder {
  separator: string;
}

const LIST_FORMAT_OPTIONS: Intl.ListFormatOptions = { style: 'long', type: 'conjunction' };

const buildChildOrderSegments = (
  childOrders: ActivityFeedChildOrder[],
  locale: string,
): ChildOrderSegment[] => {
  const hrefByCode = new Map(childOrders.map(({ code, href }) => [code, href]));

  return new Intl.ListFormat(locale, LIST_FORMAT_OPTIONS)
    .formatToParts(childOrders.map(({ code }) => code))
    .reduce<ChildOrderSegment[]>((segments, part) => {
      if (part.type === 'element') {
        return [
          ...segments,
          { code: part.value, href: hrefByCode.get(part.value) ?? null, separator: '' },
        ];
      }

      const lastSegment = segments[segments.length - 1];

      if (!lastSegment) {
        return segments;
      }

      return [...segments.slice(0, -1), { ...lastSegment, separator: part.value }];
    }, []);
};

const ActivityFeedChildOrders = ({ childOrders }: ActivityFeedChildOrdersProps) => {
  const { locale } = useTranslation();

  return (
    <>
      {buildChildOrderSegments(childOrders, locale).map(({ code, href, separator }) => (
        <Fragment key={code}>
          {href ? (
            <Typography
              variant="inherit"
              to={href}
              sx={{ color: 'inherit', textDecoration: 'underline' }}
            >
              {code}
            </Typography>
          ) : (
            code
          )}
          {separator}
        </Fragment>
      ))}
    </>
  );
};

export default ActivityFeedChildOrders;
